/**
 * "List your car" landing page (Iter 67i, i18n-refactored Iter 69).
 *
 * Comprehensive value-proposition page for prospective hosts. All copy
 * routes through i18next under the `becomeVendor.*` namespace. The page
 * structure, palette and testids match the pre-i18n version; only the
 * text is parameterised.
 */
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import VendorCityStrip from "@/components/vendor/VendorCityStrip";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, BadgeCheck, Sparkles,
  Car, CalendarDays, ShieldCheck, BadgePercent, Tag, MessageSquare, FileText,
  Receipt, Banknote, MapPinned, Bell, BarChart3, Globe2, ClipboardCheck,
  Wallet, Users, Star, Lock, Headphones, Building2, ChevronRight,
} from "lucide-react";

// Feature blueprint — text now comes from i18n keys at render time.
const FEATURES = [
  { key: "listings",   icon: Car,            palette: "sky" },
  { key: "calendar",   icon: CalendarDays,   palette: "indigo" },
  { key: "insurance",  icon: ShieldCheck,    palette: "emerald" },
  { key: "pricing",    icon: BadgePercent,   palette: "violet" },
  { key: "addons",     icon: Tag,            palette: "fuchsia" },
  { key: "venues",     icon: MapPinned,      palette: "rose" },
  { key: "agreements", icon: FileText,       palette: "teal" },
  { key: "invoices",   icon: Receipt,        palette: "cyan" },
  { key: "payments",   icon: Wallet,         palette: "amber" },
  { key: "messaging",  icon: MessageSquare,  palette: "blue" },
  { key: "dashboard",  icon: BarChart3,      palette: "lime" },
  { key: "alerts",     icon: Bell,           palette: "orange" },
];

const PALETTE_MAP = {
  sky:      { bg: "from-sky-50 via-white to-indigo-50/60",         ring: "border-sky-100 ring-sky-100",         iconBg: "bg-sky-50",       iconFg: "text-sky-600" },
  indigo:   { bg: "from-indigo-50 via-white to-blue-50/60",        ring: "border-indigo-100 ring-indigo-100",   iconBg: "bg-indigo-50",    iconFg: "text-indigo-600" },
  emerald:  { bg: "from-emerald-50 via-white to-teal-50/60",       ring: "border-emerald-100 ring-emerald-100", iconBg: "bg-emerald-50",   iconFg: "text-emerald-600" },
  violet:   { bg: "from-violet-50 via-white to-fuchsia-50/60",     ring: "border-violet-100 ring-violet-100",   iconBg: "bg-violet-50",    iconFg: "text-violet-600" },
  fuchsia:  { bg: "from-fuchsia-50 via-white to-pink-50/60",       ring: "border-fuchsia-100 ring-fuchsia-100", iconBg: "bg-fuchsia-50",   iconFg: "text-fuchsia-600" },
  rose:     { bg: "from-rose-50 via-white to-pink-50/60",          ring: "border-rose-100 ring-rose-100",       iconBg: "bg-rose-50",      iconFg: "text-rose-600" },
  teal:     { bg: "from-teal-50 via-white to-emerald-50/60",       ring: "border-teal-100 ring-teal-100",       iconBg: "bg-teal-50",      iconFg: "text-teal-600" },
  cyan:     { bg: "from-cyan-50 via-white to-sky-50/60",           ring: "border-cyan-100 ring-cyan-100",       iconBg: "bg-cyan-50",      iconFg: "text-cyan-600" },
  amber:    { bg: "from-amber-50 via-white to-orange-50/60",       ring: "border-amber-100 ring-amber-100",     iconBg: "bg-amber-50",     iconFg: "text-amber-600" },
  blue:     { bg: "from-blue-50 via-white to-sky-50/60",           ring: "border-blue-100 ring-blue-100",       iconBg: "bg-blue-50",      iconFg: "text-blue-600" },
  lime:     { bg: "from-lime-50 via-white to-green-50/60",         ring: "border-lime-100 ring-lime-100",       iconBg: "bg-lime-50",      iconFg: "text-lime-700" },
  orange:   { bg: "from-orange-50 via-white to-amber-50/60",       ring: "border-orange-100 ring-orange-100",   iconBg: "bg-orange-50",    iconFg: "text-orange-600" },
};

const STEPS = [
  { key: "apply",    icon: ClipboardCheck },
  { key: "approved", icon: Building2 },
  { key: "list",     icon: Car },
  { key: "earn",     icon: Banknote },
];

const TRUST = [
  { key: "vetted",    icon: Lock,       color: "indigo" },
  { key: "twoDriver", icon: Users,      color: "fuchsia" },
  { key: "multiCcy",  icon: Globe2,     color: "sky" },
  { key: "support",   icon: Headphones, color: "rose" },
];

function FeatureCard({ feature, idx }) {
  const { t } = useTranslation();
  const p = PALETTE_MAP[feature.palette] || PALETTE_MAP.sky;
  return (
    <div
      className={`group relative p-6 rounded-3xl border bg-gradient-to-br ${p.bg} ${p.ring} hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300`}
      style={{ animationDelay: `${idx * 40}ms` }}
      data-testid={`vendor-feature-${idx}`}
    >
      <div className={`h-11 w-11 rounded-2xl ${p.iconBg} ring-1 ${p.ring} flex items-center justify-center mb-4 shadow-sm`}>
        <feature.icon className={`h-5 w-5 ${p.iconFg}`} aria-hidden="true" />
      </div>
      <div className="font-display text-lg font-medium leading-snug mb-2">
        {t(`becomeVendor.features.items.${feature.key}.title`)}
      </div>
      <div className="text-sm text-neutral-600 leading-relaxed">
        {t(`becomeVendor.features.items.${feature.key}.text`)}
      </div>
    </div>
  );
}

export default function BecomeVendor() {
  const { t } = useTranslation();

  return (
    <div>
      <Header />

      {/* ───────────────────── Hero ───────────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-sky-50 via-white to-violet-50/60" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 lg:pt-24 pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[10px] font-bold uppercase tracking-wider shadow-sm mb-5">
                <Sparkles className="h-3 w-3" /> {t("becomeVendor.hero.pill")}
              </div>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tighter font-semibold leading-[1.05]">
                {t("becomeVendor.hero.titleLine1")} <br />
                <span className="text-neutral-400">{t("becomeVendor.hero.titleLine2")}</span>
              </h1>
              <p className="text-lg text-neutral-600 mt-6 max-w-xl leading-relaxed">
                {t("becomeVendor.hero.subtitle")}
              </p>
              <div className="mt-8 flex flex-wrap gap-3 items-center">
                <Link to="/register?role=vendor">
                  <Button
                    className="rounded-full h-12 px-7 bg-gradient-to-r from-sky-600 via-indigo-600 to-violet-600 hover:from-sky-700 hover:via-indigo-700 hover:to-violet-700 text-white shadow-lg shadow-indigo-500/20"
                    data-testid="become-vendor-cta"
                  >
                    {t("becomeVendor.hero.ctaPrimary")} <ArrowRight className="h-4 w-4 ml-1.5" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="ghost" className="rounded-full h-12 px-6" data-testid="become-vendor-login">
                    {t("becomeVendor.hero.ctaSecondary")}
                  </Button>
                </Link>
              </div>
              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-neutral-600">
                <span className="inline-flex items-center gap-1.5"><BadgeCheck className="h-4 w-4 text-emerald-600" /> {t("becomeVendor.hero.badgeNoListingFee")}</span>
                <span className="inline-flex items-center gap-1.5"><BadgeCheck className="h-4 w-4 text-emerald-600" /> {t("becomeVendor.hero.badgeApproved")}</span>
                <span className="inline-flex items-center gap-1.5"><BadgeCheck className="h-4 w-4 text-emerald-600" /> {t("becomeVendor.hero.badgeCancel")}</span>
              </div>
            </div>
            <div className="aspect-[4/5] rounded-3xl bg-neutral-100 overflow-hidden shadow-xl shadow-indigo-500/10">
              <img
                src="https://images.unsplash.com/photo-1621441916074-96050a7b1592?auto=format&fit=crop&w=900&q=80"
                alt={t("becomeVendor.hero.imageAlt")}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Iter 67j: rotating credibility strip. */}
          <VendorCityStrip />
        </div>
      </section>

      {/* ───────────────────── Features grid ───────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="vendor-features-section">
        <div className="max-w-3xl mb-12">
          <div className="text-xs uppercase tracking-[0.2em] text-indigo-600 font-semibold mb-4">{t("becomeVendor.features.eyebrow")}</div>
          <h2 className="font-display text-3xl lg:text-5xl tracking-tight font-medium">
            {t("becomeVendor.features.title")}
          </h2>
          <p className="mt-4 text-neutral-600 text-lg leading-relaxed">
            {t("becomeVendor.features.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {FEATURES.map((f, i) => <FeatureCard key={f.key} feature={f} idx={i} />)}
        </div>
      </section>

      {/* ───────────────────── How it works ───────────────────── */}
      <section className="bg-neutral-50 border-y border-border/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-600 font-semibold mb-4">{t("becomeVendor.steps.eyebrow")}</div>
            <h2 className="font-display text-3xl lg:text-4xl tracking-tight font-medium">
              {t("becomeVendor.steps.title")}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((s, i) => (
              <div
                key={s.key}
                className="relative p-6 rounded-3xl bg-white border border-border/60 hover:shadow-md transition"
                data-testid={`vendor-step-${i + 1}`}
              >
                <div className="absolute -top-3 -left-3 h-9 w-9 rounded-2xl bg-gradient-to-br from-sky-600 to-indigo-600 text-white font-bold flex items-center justify-center shadow-lg shadow-indigo-500/30 text-sm">
                  {i + 1}
                </div>
                <div className="h-10 w-10 rounded-xl bg-indigo-50 ring-1 ring-indigo-100 flex items-center justify-center mb-4">
                  <s.icon className="h-5 w-5 text-indigo-600" />
                </div>
                <div className="font-display text-lg font-medium mb-1">{t(`becomeVendor.steps.items.${s.key}.label`)}</div>
                <div className="text-sm text-neutral-600 leading-relaxed">{t(`becomeVendor.steps.items.${s.key}.text`)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────────── Earnings snapshot ───────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="rounded-3xl border border-indigo-100 bg-gradient-to-br from-sky-50 via-white to-violet-50/40 p-8 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-10 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-violet-600 font-semibold mb-4">{t("becomeVendor.earnings.eyebrow")}</div>
              <h2 className="font-display text-3xl lg:text-4xl tracking-tight font-medium leading-tight">
                {t("becomeVendor.earnings.title")}
              </h2>
              <p className="mt-4 text-neutral-600 leading-relaxed">
                {t("becomeVendor.earnings.subtitle")}
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                <span className="px-3 py-1 rounded-full bg-white border border-emerald-100 text-emerald-700 text-xs font-semibold">{t("becomeVendor.earnings.chipFee")}</span>
                <span className="px-3 py-1 rounded-full bg-white border border-sky-100 text-sky-700 text-xs font-semibold">{t("becomeVendor.earnings.chipMonthly")}</span>
                <span className="px-3 py-1 rounded-full bg-white border border-violet-100 text-violet-700 text-xs font-semibold">{t("becomeVendor.earnings.chipNoContract")}</span>
              </div>
            </div>

            <div className="rounded-2xl bg-white border border-border p-6 shadow-sm">
              <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold mb-4">{t("becomeVendor.earnings.sampleHeading")}</div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-neutral-600">{t("becomeVendor.earnings.rentalTotal")}</span><span className="font-medium">€200.00</span></div>
                <div className="flex justify-between"><span className="text-neutral-600">{t("becomeVendor.earnings.platformFee")}</span><span className="font-medium text-rose-600">− €20.00</span></div>
                <div className="border-t border-dashed border-neutral-200" />
                <div className="flex justify-between text-base">
                  <span className="font-semibold text-neutral-900">{t("becomeVendor.earnings.youReceive")}</span>
                  <span className="font-display font-semibold text-emerald-600">€180.00</span>
                </div>
                <div className="text-[11px] text-neutral-500 leading-relaxed pt-2 border-t border-dashed border-neutral-200">
                  {t("becomeVendor.earnings.note")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────────── Trust strip ───────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {TRUST.map((tr) => {
            const p = PALETTE_MAP[tr.color] || PALETTE_MAP.sky;
            const label = t(`becomeVendor.trust.${tr.key}.label`);
            return (
              <div key={tr.key} className="p-5 rounded-2xl bg-white border border-border/60 hover:shadow-md transition" data-testid={`vendor-trust-${tr.key}`}>
                <div className={`h-9 w-9 rounded-xl ${p.iconBg} ring-1 ${p.ring} flex items-center justify-center mb-3`}>
                  <tr.icon className={`h-4 w-4 ${p.iconFg}`} />
                </div>
                <div className="font-medium text-sm mb-1">{label}</div>
                <div className="text-xs text-neutral-500 leading-relaxed">{t(`becomeVendor.trust.${tr.key}.text`)}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ───────────────────── Final CTA band ───────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="rounded-3xl bg-gradient-to-br from-sky-600 via-indigo-600 to-violet-600 text-white p-10 lg:p-14 relative overflow-hidden">
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/10 blur-3xl" aria-hidden="true" />
          <div className="absolute -left-20 -bottom-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" aria-hidden="true" />
          <div className="relative grid grid-cols-1 lg:grid-cols-[1.4fr_auto] gap-8 items-center">
            <div>
              <h2 className="font-display text-3xl lg:text-4xl tracking-tight font-medium leading-tight">
                {t("becomeVendor.finalCta.title")}
              </h2>
              <p className="mt-3 text-white/85 max-w-2xl">
                {t("becomeVendor.finalCta.subtitle")}
              </p>
              <div className="mt-3 inline-flex items-center gap-1.5 text-sm">
                <Star className="h-4 w-4 fill-amber-300 text-amber-300" />
                <span className="text-white/90">{t("becomeVendor.finalCta.stars")}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/register?role=vendor">
                <Button className="rounded-full h-12 px-7 bg-white text-indigo-700 hover:bg-white/90" data-testid="become-vendor-cta-bottom">
                  {t("becomeVendor.finalCta.ctaPrimary")} <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="ghost" className="rounded-full h-12 px-6 text-white hover:bg-white/10" data-testid="become-vendor-contact-link">
                  {t("becomeVendor.finalCta.ctaSecondary")}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
